import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Add to Bag modal: slide-out from right (like mini Shopping Bag).
 * Loads mini product content [data-mini-product-content] from product URL ?view=mini for
 * variant selection and add to cart. Buttons are moved to a fixed footer.
 */
class AddToBagModal extends DialogComponent {
  static requiredRefs = ['dialog'];

  #abortController = null;
  #contentCache = new Map();

  /** @type {string} Selector for the mini product wrapper (single source of truth for modal content). */
  static #miniProductSelector = '[data-mini-product-content]';
  /** Fallback: class when attribute might be stripped (e.g. in some theme wrappers). */
  static #miniProductClassSelector = '.mini-product';

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleOpenClick, true);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('click', this.#handleFooterClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleOpenClick, true);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('click', this.#handleFooterClick, true);
    this.#abortController?.abort();
  }

  /**
   * Footer click: proxy "Add to Bag" triggers the real submit button (kept in content) so
   * product-form-component refs stay valid. Capture phase.
   */
  #handleFooterClick = (e) => {
    const footer = document.getElementById('add-to-bag-modal-footer');
    if (!footer?.contains(e.target)) return;

    const proxyBtn = e.target.closest?.('[data-add-to-bag-trigger]');
    if (!proxyBtn) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    const contentEl = document.getElementById('add-to-bag-modal-content');
    const realSubmit = contentEl?.querySelector?.('form[data-type="add-to-cart-form"] button[type="submit"][name="add"]');
    if (realSubmit instanceof HTMLButtonElement) {
      realSubmit.click();
    }
  };

  close() {
    this.closeDialog();
  }

  #handleCartUpdate = () => {
    if (this.refs.dialog?.open) {
      this.closeDialog();
    }
  };

  #handleOpenClick = (e) => {
    const trigger = e.target?.closest?.('[data-open-add-to-bag]');
    if (!trigger || !trigger.dataset.productUrl) return;
    e.preventDefault();
    e.stopPropagation();
    this.openWithProduct(trigger.dataset.productUrl);
  };

  async openWithProduct(productUrl) {
    const contentEl = document.getElementById('add-to-bag-modal-content');
    const footerEl = document.getElementById('add-to-bag-modal-footer');
    if (!contentEl || !footerEl) return;

    this.#showLoading(contentEl);
    footerEl.hidden = true;
    footerEl.querySelector('.add-to-bag-modal__footer-buttons')?.replaceChildren();

    if (this.refs.dialog && !this.refs.dialog.open) {
      this.showDialog();
    }

    const baseUrl = productUrl.split('?')[0];
    const miniProductUrl = baseUrl + (baseUrl.includes('?') ? '&' : '?') + 'view=mini';
    let miniProductEl = this.#contentCache.get(miniProductUrl);
    if (!miniProductEl) {
      miniProductEl = await this.#loadMiniProduct(baseUrl, miniProductUrl);
      if (miniProductEl) this.#contentCache.set(miniProductUrl, miniProductEl);
    }

    if (!miniProductEl) {
      this.#showError(contentEl);
      return;
    }

    const freshContent = miniProductEl.cloneNode(true);
    morph(contentEl, freshContent);

    this.#stripContentAfterOptions(contentEl);
    const moved = this.#moveButtonsToFooter(contentEl, footerEl);
    footerEl.hidden = !moved;
  }

  /**
   * Find mini product root in a document: try data attribute first, then class.
   * @param {Document} doc - Parsed HTML document
   * @returns {Element | null}
   */
  #findMiniProductInDocument(doc) {
    return doc.querySelector(AddToBagModal.#miniProductSelector) ||
      doc.querySelector(AddToBagModal.#miniProductClassSelector);
  }

  /**
   * Load mini product HTML: try full page (?view=mini), then Section Rendering API fallback.
   * @param {string} baseUrl - Product URL without query (e.g. /products/handle)
   * @param {string} cacheKey - Key for cache (mini URL)
   * @returns {Promise<Element | null>} Cloned element or null
   */
  async #loadMiniProduct(baseUrl, cacheKey) {
    const fullPageUrl = baseUrl + (baseUrl.includes('?') ? '&' : '?') + 'view=mini';
    const html = await this.#fetchProductPage(fullPageUrl);
    if (html) {
      const found = this.#findMiniProductInDocument(html);
      if (found) return found.cloneNode(true);
    }

    const sectionUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}section_id=main&view=mini`;
    const sectionHtml = await this.#fetchProductPage(sectionUrl);
    if (sectionHtml) {
      const found = this.#findMiniProductInDocument(sectionHtml);
      if (found) return found.cloneNode(true);
    }

    return null;
  }

  /**
   * Remove all content below the buy buttons: payment icons, "Why Choose", "Product Highlights", etc.
   * @param {HTMLElement} contentEl - #add-to-bag-modal-content
   */
  #stripContentAfterOptions(contentEl) {
    const buyBlock = contentEl.querySelector('.buy-buttons-block');
    if (buyBlock?.parentElement) {
      let next = buyBlock.nextElementSibling;
      while (next) {
        const toRemove = next;
        next = next.nextElementSibling;
        toRemove.remove();
      }
    }
    contentEl.querySelectorAll(
      '.product-rewards-block, .product-rewards, ' +
      '.product-accordion-group, .product-accordion, .payment-icons, .payment-icons__list, ' +
      '.product-description-with-read-more, .product-info-accordions'
    ).forEach((el) => el.remove());
    contentEl.querySelectorAll('.shopify-payment-button__more-options, more-payment-options-link').forEach((el) => el.remove());
    contentEl.querySelectorAll('a[href*="payment"]').forEach((el) => {
      if (/more\s*payment|payment\s*options/i.test(el.textContent || '')) el.remove();
    });
  }

  /**
   * Keep Add to Cart in content (hidden). Add proxy "Add to Bag" and wishlist in footer so
   * product-form-component refs stay valid and handleSubmit works.
   */
  #moveButtonsToFooter(contentEl, footerEl) {
    const container = contentEl.querySelector('.buy-buttons-container');
    const footerButtons = footerEl.querySelector('.add-to-bag-modal__footer-buttons');
    if (!container || !footerButtons) return false;

    footerButtons.replaceChildren();

    const wishlistBtn = this.#createWishlistButton(contentEl);
    if (wishlistBtn) footerButtons.appendChild(wishlistBtn);

    const proxyAddBtn = this.#createProxyAddToBagButton();
    footerButtons.appendChild(proxyAddBtn);

    container.classList.add('add-to-bag-modal__content-buttons-hidden');
    return true;
  }

  #createWishlistButton(contentEl) {
    const productIdEl = contentEl?.querySelector?.('[data-product-id]');
    const productId = productIdEl?.getAttribute?.('data-product-id');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'add-to-bag-modal__footer-wishlist';
    btn.setAttribute('aria-label', 'Add to wishlist');
    btn.setAttribute('data-wishlist-toggle', '');
    if (productId) btn.setAttribute('data-product-id', productId);
    const inWishlist = productId && this.#isProductInWishlist(productId);
    if (inWishlist) btn.classList.add('is-active');
    btn.innerHTML = '<span class="wishlist-icon__svg svg-wrapper" aria-hidden="true"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path class="wishlist-icon__heart" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg></span>';
    return btn;
  }

  #isProductInWishlist(productId) {
    try {
      const raw = localStorage.getItem('wishlistProducts');
      const stored = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(stored)) return false;
      const id = String(productId);
      return stored.some((item) => (typeof item === 'object' && item != null ? String(item.id) : String(item)) === id);
    } catch {
      return false;
    }
  }

  #createProxyAddToBagButton() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-add-to-bag-trigger', '');
    btn.className = 'add-to-bag-modal__footer-add button add-to-cart-button';
    btn.innerHTML = '<span class="add-to-cart-text"><span class="svg-wrapper add-to-cart-icon">' +
      '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg></span>' +
      '<span class="add-to-cart-text__content">Add To Bag</span></span>';
    return btn;
  }

  #showLoading(contentEl) {
    contentEl.innerHTML = '';
    const loading = document.createElement('div');
    loading.className = 'add-to-bag-modal__loading';
    loading.id = 'add-to-bag-modal-loading';
    loading.setAttribute('aria-hidden', 'true');
    loading.textContent = 'Loading…';
    contentEl.appendChild(loading);
  }

  #showError(contentEl) {
    contentEl.innerHTML = '';
    const err = document.createElement('p');
    err.className = 'add-to-bag-modal__error';
    err.textContent = 'Unable to load product. Please try again or view the product page.';
    contentEl.appendChild(err);
  }

  async #fetchProductPage(productUrl) {
    if (!productUrl) return null;
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    try {
      const response = await fetch(productUrl, { signal: this.#abortController.signal });
      if (!response.ok) return null;
      const text = await response.text();
      return new DOMParser().parseFromString(text, 'text/html');
    } catch (err) {
      if (err?.name !== 'AbortError') console.error(err);
      return null;
    } finally {
      this.#abortController = null;
    }
  }
}

if (!customElements.get('add-to-bag-modal')) {
  customElements.define('add-to-bag-modal', AddToBagModal);
}
